<template>
    <div class="vital-signs-charts">
        <!-- Gráfica de Peso -->
        <div v-if="weightData.data.length > 0" class="chart-card weight-card">
            <div class="chart-header">
                <div class="header-left">
                    <div class="icon-wrapper weight-gradient">
                        <i class="fas fa-weight"></i>
                    </div>
                    <div class="header-info">
                        <h4 class="chart-title">Peso</h4>
                        <p class="chart-count">{{ weightData.data.length }} registros</p>
                    </div>
                </div>
                <div v-if="weightData.current" class="current-badge weight-bg">
                    <span class="value">{{ weightData.current }}</span>
                    <span class="unit">kg</span>
                </div>
            </div>
            <div class="chart-body">
                <canvas :id="`weight-chart-${uniqueId}`" height="180"></canvas>
            </div>
        </div>

        <!-- Gráfica de Talla -->
        <div v-if="heightData.data.length > 0" class="chart-card height-card">
            <div class="chart-header">
                <div class="header-left">
                    <div class="icon-wrapper height-gradient">
                        <i class="fas fa-ruler-vertical"></i>
                    </div>
                    <div class="header-info">
                        <h4 class="chart-title">Talla</h4>
                        <p class="chart-count">{{ heightData.data.length }} registros</p>
                    </div>
                </div>
                <div v-if="heightData.current" class="current-badge height-bg">
                    <span class="value">{{ heightData.current }}</span>
                    <span class="unit">cm</span>
                </div>
            </div>
            <div class="chart-body">
                <canvas :id="`height-chart-${uniqueId}`" height="180"></canvas>
            </div>
        </div>

        <!-- Gráfica de IMC -->
        <div v-if="bmiData.data.length > 0" class="chart-card bmi-card full-width">
            <div class="chart-header">
                <div class="header-left">
                    <div class="icon-wrapper bmi-gradient">
                        <i class="fas fa-calculator"></i>
                    </div>
                    <div class="header-info">
                        <h4 class="chart-title">Índice de Masa Corporal</h4>
                        <p class="chart-count">{{ bmiData.data.length }} registros</p>
                    </div>
                </div>
                <div v-if="bmiData.current" class="current-badge bmi-bg">
                    <span class="value">{{ bmiData.current }}</span>
                    <span class="unit">kg/m²</span>
                </div>
            </div>
            <div class="chart-body">
                <canvas :id="`bmi-chart-${uniqueId}`" height="200"></canvas>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import Chart from 'chart.js/auto';

const props = defineProps({
    weightData: {
        type: Object,
        required: true,
        default: () => ({ labels: [], data: [], current: null })
    },
    heightData: {
        type: Object,
        required: true,
        default: () => ({ labels: [], data: [], current: null })
    },
    bmiData: {
        type: Object,
        required: true,
        default: () => ({ labels: [], data: [], current: null })
    }
});

// ID único para evitar conflictos
const uniqueId = ref(Math.random().toString(36).substr(2, 9));

// Referencias a las gráficas
const charts = ref({
    weight: null,
    height: null,
    bmi: null
});

// Configuración común para todas las gráficas
const getCommonChartOptions = (color, unit) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
        duration: 1500,
        easing: 'easeInOutQuart',
        delay: (context) => {
            return context.dataIndex * 50; // Animación escalonada
        }
    },
    interaction: {
        mode: 'index',
        intersect: false
    },
    plugins: {
        legend: { display: false },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            padding: 16,
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: color,
            borderWidth: 2,
            displayColors: false,
            cornerRadius: 8,
            titleFont: {
                size: 13,
                weight: '600'
            },
            bodyFont: {
                size: 14,
                weight: '700'
            },
            callbacks: {
                label: (context) => `${context.parsed.y}${unit}`
            }
        }
    },
    scales: {
        x: {
            grid: {
                display: false,
                drawBorder: false
            },
            ticks: {
                maxRotation: 45,
                minRotation: 45,
                font: {
                    size: 11,
                    weight: '500'
                },
                color: '#64748b',
                padding: 8
            }
        },
        y: {
            beginAtZero: false,
            grid: {
                color: 'rgba(148, 163, 184, 0.08)',
                drawBorder: false,
                lineWidth: 1
            },
            ticks: {
                font: {
                    size: 12,
                    weight: '500'
                },
                color: '#475569',
                padding: 12,
                callback: (value) => `${value}${unit}`
            }
        }
    }
});

// Crear gráfica con animación
const createChart = (canvasId, data, labels, color, unit) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
        console.error(`Canvas ${canvasId} no encontrado`);
        return null;
    }

    const ctx = canvas.getContext('2d');

    // Crear gradiente animado
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    // Convertir rgb(r, g, b) a rgba(r, g, b, a)
    const rgbaStart = color.replace('rgb', 'rgba').replace(')', ', 0.25)');
    const rgbaEnd = color.replace('rgb', 'rgba').replace(')', ', 0)');
    gradient.addColorStop(0, rgbaStart); // 25% opacidad
    gradient.addColorStop(1, rgbaEnd); // 0% opacidad

    return new Chart(ctx, {
        type: 'line',
        data: {
            labels: labels,
            datasets: [{
                label: '',
                data: data,
                borderColor: color,
                backgroundColor: gradient,
                borderWidth: 3,
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 8,
                pointBackgroundColor: color,
                pointBorderColor: '#fff',
                pointBorderWidth: 3,
                pointHoverBackgroundColor: color,
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 3,
                // Animación de puntos
                segment: {
                    // eslint-disable-next-line no-unused-vars
                    borderColor: ctx => {
                        return color;
                    }
                }
            }]
        },
        options: getCommonChartOptions(color, unit)
    });
};

// Renderizar todas las gráficas
const renderCharts = () => {
    setTimeout(() => {
        // Destruir gráficas anteriores
        Object.values(charts.value).forEach(chart => {
            if (chart) chart.destroy();
        });

        // Peso
        if (props.weightData.data.length > 0) {
            charts.value.weight = createChart(
                `weight-chart-${uniqueId.value}`,
                props.weightData.data,
                props.weightData.labels,
                'rgb(99, 102, 241)', // Púrpura
                ' kg'
            );
        }

        // Talla
        if (props.heightData.data.length > 0) {
            charts.value.height = createChart(
                `height-chart-${uniqueId.value}`,
                props.heightData.data,
                props.heightData.labels,
                'rgb(16, 185, 129)', // Verde
                ' cm'
            );
        }

        // IMC
        if (props.bmiData.data.length > 0) {
            charts.value.bmi = createChart(
                `bmi-chart-${uniqueId.value}`,
                props.bmiData.data,
                props.bmiData.labels,
                'rgb(251, 146, 60)', // Naranja
                ''
            );
        }
    }, 100);
};

// Watchers para actualizar gráficas cuando cambien los datos
watch([() => props.weightData, () => props.heightData, () => props.bmiData], () => {
    renderCharts();
}, { deep: true });

onMounted(() => {
    renderCharts();
});
</script>

<style scoped>
.vital-signs-charts {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
    padding: 0;
    animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.chart-card {
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-radius: 20px;
    border: 1px solid rgba(148, 163, 184, 0.12);
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}

.chart-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, currentColor, transparent);
    opacity: 0;
    transition: opacity 0.4s ease;
}

.weight-card::before {
    color: rgb(99, 102, 241);
}

.height-card::before {
    color: rgb(16, 185, 129);
}

.bmi-card::before {
    color: rgb(251, 146, 60);
}

.chart-card:hover {
    transform: translateY(-6px) scale(1.01);
    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.1),
        0 10px 20px -5px rgba(0, 0, 0, 0.08);
    border-color: rgba(99, 102, 241, 0.25);
}

.chart-card:hover::before {
    opacity: 1;
}

.full-width {
    grid-column: 1 / -1;
}

.chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px;
    background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%);
    border-bottom: 1px solid rgba(148, 163, 184, 0.08);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: white;
    box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
    animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }
}

.weight-gradient {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
}

.height-gradient {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.bmi-gradient {
    background: linear-gradient(135deg, #fb923c 0%, #f97316 100%);
}

.chart-card:hover .icon-wrapper {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.3);
}

.header-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.chart-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
}

.chart-count {
    margin: 0;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.current-badge {
    padding: 12px 20px;
    border-radius: 14px;
    display: flex;
    align-items: baseline;
    gap: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    animation: badgeFloat 3s ease-in-out infinite;
}

@keyframes badgeFloat {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-3px);
    }
}

.weight-bg {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
    border: 2px solid rgba(99, 102, 241, 0.3);
}

.height-bg {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
    border: 2px solid rgba(16, 185, 129, 0.3);
}

.bmi-bg {
    background: linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%);
    border: 2px solid rgba(251, 146, 60, 0.3);
}

.chart-card:hover .current-badge {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.value {
    font-size: 26px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.unit {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
}

.chart-body {
    padding: 28px;
    background: white;
    position: relative;
}

/* Responsive */
@media (max-width: 1200px) {
    .vital-signs-charts {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .full-width {
        grid-column: 1;
    }
}

@media (max-width: 768px) {
    .chart-header {
        padding: 20px;
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
    }

    .header-left {
        width: 100%;
    }

    .current-badge {
        align-self: flex-end;
    }

    .chart-body {
        padding: 20px 16px;
    }

    .icon-wrapper {
        width: 48px;
        height: 48px;
        font-size: 20px;
    }

    .chart-title {
        font-size: 16px;
    }

    .value {
        font-size: 22px;
    }
}
</style>
